/* eslint-disable no-nested-ternary */
"use client";

import { useMessages } from "@/lib/i18n/useMessages";
import { Box, GlobalStyles, IconButton, useTheme } from "@mui/material";

import {
  ArrowBackIosNewRounded,
  ArrowForwardIosRounded,
} from "@mui/icons-material";
import { useMemo, useRef, useState } from "react";
import Slider, { Settings } from "react-slick";

import {
  HowManyCards,
  getCarouselResponsivePresets,
} from "./slCarouselResponsivePresets";

export { HowManyCards } from "./slCarouselResponsivePresets";

const slickThemeGlobalStyles = {
  ".slick-prev, .slick-next": {
    fontSize: 0,
    lineHeight: 0,
    position: "absolute",
    top: "50%",
    display: "block",
    width: 20,
    height: 20,
    padding: 0,
    WebkitTransform: "translate(0, -50%)",
    msTransform: "translate(0, -50%)",
    transform: "translate(0, -50%)",
    cursor: "pointer",
    color: "transparent",
    border: "none",
    outline: "none",
    background: "transparent",
  },
  ".slick-prev:hover, .slick-prev:focus, .slick-next:hover, .slick-next:focus":
    {
      color: "transparent",
      outline: "none",
      background: "transparent",
    },
  ".slick-prev:hover:before, .slick-prev:focus:before, .slick-next:hover:before, .slick-next:focus:before":
    {
      opacity: 1,
    },
  ".slick-prev.slick-disabled:before, .slick-next.slick-disabled:before": {
    opacity: 0.25,
  },
  ".slick-prev:before, .slick-next:before": {
    fontFamily: "slick",
    fontSize: 20,
    lineHeight: 1,
    opacity: 0.75,
    color: "white",
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
  },
  '[dir="rtl"] .slick-prev': {
    left: "auto",
  },
  ".slick-prev:before": {
    content: '"←"',
  },
  '[dir="rtl"] .slick-prev:before': {
    content: '"→"',
  },
  ".slick-prev": {
    left: 25,
  },
  '[dir="rtl"] .slick-next': {
    right: "auto",
    left: -25,
  },
  ".slick-next:before": {
    content: '"→"',
  },
  '[dir="rtl"] .slick-next:before': {
    content: '"←"',
  },
  ".slick-dotted.slick-slider": {
    marginBottom: 30,
  },
  ".slick-dots": {
    position: "absolute",
    bottom: -25,
    display: "block",
    width: "100%",
    padding: 0,
    margin: 0,
    listStyle: "none",
    textAlign: "center",
  },
  ".slick-dots li": {
    position: "relative",
    display: "inline-block",
    width: 20,
    height: 20,
    margin: "0 5px",
    padding: 0,
    cursor: "pointer",
  },
  ".slick-dots li button": {
    fontSize: 0,
    lineHeight: 0,
    display: "block",
    width: 20,
    height: 20,
    padding: 5,
    cursor: "pointer",
    color: "transparent",
    border: 0,
    outline: "none",
    background: "transparent",
  },
  ".slick-dots li button:hover, .slick-dots li button:focus": {
    outline: "none",
  },
  ".slick-dots li button:hover:before, .slick-dots li button:focus:before": {
    opacity: 1,
  },
  ".slick-dots li button:before": {
    fontFamily: "slick",
    fontSize: 6,
    lineHeight: "20px",
    position: "absolute",
    top: 0,
    left: 0,
    width: 20,
    height: 20,
    content: '"•"',
    textAlign: "center",
    opacity: 0.25,
    color: "black",
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
  },
  ".slick-dots li.slick-active button:before": {
    opacity: 0.75,
    color: "black",
  },
};

const isAtEndSlide = (activeSlide: number, itemCount: number): boolean =>
  activeSlide === itemCount - 1.5 ||
  activeSlide === itemCount - 1.1 ||
  activeSlide === itemCount - 1.2 ||
  activeSlide === itemCount - 1.3 ||
  activeSlide === itemCount - 1.4 ||
  activeSlide === itemCount - 1.5;

export const SLCarousel = ({
  howManyCards = HowManyCards.OneToFive,
  items,
  slickSliderSettings,
  slidesToShow,
}: {
  items: React.ReactNode[];
  howManyCards?: HowManyCards;
  slickSliderSettings?: Settings;
  slidesToShow?: number;
}) => {
  const { common } = useMessages();
  const theme = useTheme();

  const sliderRef = useRef<Slider>(null);
  const handleNext = () => {
    sliderRef.current?.slickNext();
  };
  const handlePrev = () => {
    sliderRef.current?.slickPrev();
  };

  const responsiveness = useMemo(
    () => getCarouselResponsivePresets(theme),
    [theme],
  );

  const [activeSlide, setActiveSlide] = useState(0);

  const settings: Settings = useMemo(
    () => ({
      afterChange: (current) => {
        setActiveSlide(current);
      },

      dots: true,
      infinite: false,
      speed: 350,
      slidesToShow: slidesToShow ?? 1.1,
      responsive: responsiveness[howManyCards],

      ...slickSliderSettings,
    }),
    [slidesToShow, responsiveness, howManyCards, slickSliderSettings],
  );

  const hidePrevArrow = items.length === 1 || activeSlide === 0;
  const hideNextArrow =
    items.length === 1 || isAtEndSlide(activeSlide, items.length);

  return (
    <Box
      sx={{
        position: "relative",
        "& .slick-arrow:before": {
          display: "none",
        },
      }}
    >
      <GlobalStyles styles={slickThemeGlobalStyles} />
      <Box
        sx={{
          position: "sticky",
          top: "50%",
          left: 0,
          pointerEvents: "none",
          ml: -3,
          my: -5,
        }}
        zIndex={1000}
      >
        <IconButton
          onClick={handlePrev}
          aria-label={common.accessibility.carouselPrevious}
          sx={{
            opacity: hidePrevArrow ? 0 : { xs: 0, sm: 1 },
            zIndex: 1000,
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "auto",
            display: "flex",
            "&": { backgroundColor: "transparent" },
            "&:hover": {
              backgroundColor: theme.palette.background.paper,
            },
          }}
        >
          <ArrowBackIosNewRounded
            sx={{
              color: theme.palette.success.main,
              fontSize: {
                xs: "2rem",
                sm: "2rem",
                md: "3rem",
                lg: "3rem",
                xl: "3rem",
              },
            }}
          />
        </IconButton>
      </Box>

      <Box
        sx={{
          position: "sticky",
          top: "50%",
          right: "10px",
          pointerEvents: "none",
          mr: -3,
          my: -5,
        }}
        zIndex={1000}
      >
        <IconButton
          onClick={handleNext}
          aria-label={common.accessibility.carouselNext}
          sx={{
            marginLeft: "auto",

            display: "flex",
            opacity: hideNextArrow ? 0 : { xs: 0, sm: 1 },
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "auto",
            "&": { backgroundColor: "transparent" },
            "&:hover": {
              backgroundColor: theme.palette.background.paper,
            },
          }}
        >
          <ArrowForwardIosRounded
            sx={{
              color: theme.palette.success.main,
              fontSize: {
                xs: "2rem",
                sm: "2rem",
                md: "3rem",
                lg: "3rem",
                xl: "3rem",
              },
            }}
          />
        </IconButton>
      </Box>
      <Box sx={{ mx: { xs: 0, sm: 4, md: 6, lg: 7, xl: 7 }, overflow: "clip" }}>
        <Slider {...settings} ref={sliderRef}>
          {items.map((item, index) => (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              height={"100%"}
            >
              {item}
            </Box>
          ))}
        </Slider>
      </Box>
    </Box>
  );
};
